import {css} from "@panda/css";

import {IRange} from "@pg-design/range-module";
import {FormProviderRHF} from "@pg-design/rhf-components-module";
import {IOption} from "@pg-design/select-module";
import {PreferencesSurveySource} from "@pg-rp/common-types";

import {HomeMatchAmplitudeLocation} from "@/features/tracking/amplitude/home_match_amplitude_tracker";

import {HOME_MATCH_PREFERENCES_SURVEY_CONTENT} from "../../constants/preferences/home_match_preferences_survey_content";
import {HomeMatchFinancingPreference} from "../../constants/preferences/home_match_preferences_values";
import {HOME_MATCH_PREFERENCES_WIZARD_CONTENT} from "../../constants/preferences/home_match_preferences_wizard_content";
import {useHomeMatchPreferencesWizardSession} from "../../hooks/preferences/use_home_match_preferences_wizard_session";
import {getHomeMatchPreferencesSurveyBuildingHeight} from "../../utils/get_home_match_preferences_survey_form_values";
import {
    HomeMatchPreferencesWizardEntry,
    HomeMatchPreferencesWizardStep,
    IHomeMatchPreferencesWizardSurvey
} from "../../utils/get_home_match_preferences_wizard_steps";
import {IHomeMatchPreferencesWizardSummaryRegion} from "../../utils/get_home_match_preferences_wizard_summary_labels";
import {getHomeMatchFloorsPreferences} from "../../utils/home_match_floors_preference";
import {HomeMatchPreferencesWizardBudgetStep} from "./HomeMatchPreferencesWizardBudgetStep";
import {HomeMatchPreferencesWizardCompletionDateStep} from "./HomeMatchPreferencesWizardCompletionDateStep";
import {HomeMatchPreferencesWizardLocationStep} from "./HomeMatchPreferencesWizardLocationStep";
import {HomeMatchPreferencesWizardModal} from "./HomeMatchPreferencesWizardModal";
import {HomeMatchPreferencesWizardPhoneStep} from "./HomeMatchPreferencesWizardPhoneStep";
import {HomeMatchPreferencesWizardRoomsStep} from "./HomeMatchPreferencesWizardRoomsStep";
import {HomeMatchPreferencesWizardSmsDescription} from "./HomeMatchPreferencesWizardSmsDescription";
import {HomeMatchPreferencesWizardSmsStep} from "./HomeMatchPreferencesWizardSmsStep";
import {HomeMatchPreferencesWizardSummaryStep} from "./HomeMatchPreferencesWizardSummaryStep";

const {BUDGET, COMPLETION_DATE, LOCATION, PHONE, ROOMS, SMS, SUMMARY} = HomeMatchPreferencesWizardStep;
const {
    BUDGET_DESCRIPTION,
    BUDGET_TITLE,
    LOCATION_DESCRIPTION,
    LOCATION_TITLE,
    PHONE_DESCRIPTION,
    PHONE_TITLE,
    ROOMS_DESCRIPTION,
    ROOMS_TITLE,
    SMS: SMS_CONTENT,
    SUMMARY: SUMMARY_CONTENT
} = HOME_MATCH_PREFERENCES_WIZARD_CONTENT;

const STEP_INTRO: Partial<Record<HomeMatchPreferencesWizardStep, {description?: string; title: string}>> = {
    [BUDGET]: {description: BUDGET_DESCRIPTION, title: BUDGET_TITLE},
    [COMPLETION_DATE]: {
        description: HOME_MATCH_PREFERENCES_WIZARD_CONTENT.COMPLETION_DATE_DESCRIPTION,
        title: HOME_MATCH_PREFERENCES_SURVEY_CONTENT.COMPLETION_DATE
    },
    [LOCATION]: {description: LOCATION_DESCRIPTION, title: LOCATION_TITLE},
    [PHONE]: {description: PHONE_DESCRIPTION, title: PHONE_TITLE},
    [ROOMS]: {description: ROOMS_DESCRIPTION, title: ROOMS_TITLE},
    [SMS]: {title: SMS_CONTENT.TITLE},
    [SUMMARY]: {description: SUMMARY_CONTENT.DESCRIPTION, title: SUMMARY_CONTENT.TITLE}
};

interface IProps {
    additionalAreas?: number[] | null;
    amplitudeLocation?: HomeMatchAmplitudeLocation;
    area?: IRange<number | null> | null;
    entry?: HomeMatchPreferencesWizardEntry;
    financingPreference: HomeMatchFinancingPreference | null;
    isOpen: boolean;
    onClose: () => void;
    onOpenFullSurvey?: () => void;
    returnsToSummary?: boolean;
    onSurveyStepsDone: () => void;
    phone: string | null;
    preferencesSurveySource: PreferencesSurveySource;
    regionDetails?: IHomeMatchPreferencesWizardSummaryRegion[];
    regionOptions: IOption<number>[];
    steps: HomeMatchPreferencesWizardStep[];
    survey: IHomeMatchPreferencesWizardSurvey | null;
    zIndex?: number;
}

export const HomeMatchPreferencesWizardSurvey = (props: IProps) => {
    const {
        additionalAreas = null,
        amplitudeLocation = HomeMatchAmplitudeLocation.DASHBOARD,
        area = null,
        isOpen,
        onOpenFullSurvey,
        regionDetails = [],
        survey,
        zIndex
    } = props;
    const {
        canGoBack,
        closeWithoutSaving,
        formMethods,
        goBack,
        isExitSaveDisabled,
        isParameterEntry,
        isPrimaryDisabled,
        showExitPrompt,
        isVisible,
        map,
        onPrimaryClick,
        phone,
        primaryLabel,
        progressStepIndex,
        saveAndClose,
        sms,
        stepCount,
        summary,
        editingStep,
        visibleStep
    } = useHomeMatchPreferencesWizardSession({...props, amplitudeLocation});

    if (!isVisible) return null;

    const stepIntro = STEP_INTRO[visibleStep];
    const stepDescription =
        visibleStep === SMS && sms.verifiedPhone ? (
            <HomeMatchPreferencesWizardSmsDescription isSubmitting={phone.isSubmitting} onChangeNumber={sms.changeNumber} phone={sms.verifiedPhone} />
        ) : (
            stepIntro?.description
        );

    return (
        <FormProviderRHF {...formMethods}>
            <HomeMatchPreferencesWizardModal
                amplitudeLocation={amplitudeLocation}
                canGoBack={canGoBack}
                // Keeps the SMS step mounted while the number is being changed.
                contentKey={sms.isMounted ? SMS : undefined}
                description={stepDescription}
                isExitSaveDisabled={isExitSaveDisabled}
                isOpen={isOpen}
                isParameterEntry={isParameterEntry}
                showExitPrompt={showExitPrompt}
                isPrimaryDisabled={isPrimaryDisabled}
                mapActions={map.actions}
                onBack={goBack}
                onCloseWithoutSaving={closeWithoutSaving}
                onExitSave={saveAndClose}
                onPrimaryClick={onPrimaryClick}
                onRequestClose={phone.isChanging ? phone.closeChange : editingStep ? goBack : undefined}
                primaryLabel={primaryLabel}
                progressStepIndex={progressStepIndex}
                step={visibleStep}
                stepCount={stepCount}
                title={stepIntro?.title}
                zIndex={zIndex}
            >
                <div className={stepStyle}>
                    {visibleStep === PHONE && (
                        <HomeMatchPreferencesWizardPhoneStep
                            onRegisterSubmit={phone.registerSubmit}
                            onSaved={phone.onSaved}
                            onSubmittingChange={phone.setIsSubmitting}
                        />
                    )}

                    {visibleStep === LOCATION && (
                        <HomeMatchPreferencesWizardLocationStep
                            isMapOpen={map.isActive}
                            onMapActiveChange={map.setIsActive}
                            onRegisterMapActions={map.registerActions}
                        />
                    )}

                    {visibleStep === BUDGET && <HomeMatchPreferencesWizardBudgetStep />}

                    {visibleStep === COMPLETION_DATE && <HomeMatchPreferencesWizardCompletionDateStep />}

                    {visibleStep === ROOMS && <HomeMatchPreferencesWizardRoomsStep />}

                    {visibleStep === SUMMARY && (
                        <HomeMatchPreferencesWizardSummaryStep
                            consent={summary.consent}
                            onChangeProperty={() => onOpenFullSurvey?.()}
                            onChangeStep={summary.onChangeStep}
                            onConsentChange={summary.setConsent}
                            summary={{
                                ...summary.saved,
                                additionalAreas,
                                area,
                                buildingHeight: getHomeMatchPreferencesSurveyBuildingHeight(survey),
                                constructionEndDate: survey?.construction_end_date ?? null,
                                floorsPreference: getHomeMatchFloorsPreferences(survey),
                                marketTypes: survey?.market_types ?? null,
                                regionDetails,
                                types: survey?.types ?? null
                            }}
                        />
                    )}

                    {sms.isMounted &&
                        sms.verifiedPhone && (
                            // Stays mounted while the number is being changed, so going back does not resend the code.
                            <div hidden={visibleStep === PHONE}>
                                <HomeMatchPreferencesWizardSmsStep
                                    key={sms.sessionKey}
                                    onCanConfirmChange={sms.setCanConfirm}
                                    onRegisterSubmit={sms.registerSubmit}
                                    onResend={sms.onResend}
                                    onSubmittingChange={phone.setIsSubmitting}
                                    onSuccess={sms.onSuccess}
                                />
                            </div>
                        )}
                </div>
            </HomeMatchPreferencesWizardModal>
        </FormProviderRHF>
    );
};

const stepStyle = css({
    display: "flex",
    flex: "1 1 auto",
    flexDirection: "column",
    gap: 2,
    minHeight: 0
});
import {css} from "@panda/css";

import {IRange} from "@pg-design/range-module";
import {FormProviderRHF} from "@pg-design/rhf-components-module";
import {IOption} from "@pg-design/select-module";
import {PreferencesSurveySource} from "@pg-rp/common-types";

import {HomeMatchAmplitudeLocation} from "@/features/tracking/amplitude/home_match_amplitude_tracker";

import {HOME_MATCH_PREFERENCES_SURVEY_CONTENT} from "../../constants/preferences/home_match_preferences_survey_content";
import {HomeMatchFinancingPreference} from "../../constants/preferences/home_match_preferences_values";
import {HOME_MATCH_PREFERENCES_WIZARD_CONTENT} from "../../constants/preferences/home_match_preferences_wizard_content";
import {useHomeMatchPreferencesWizardSession} from "../../hooks/preferences/use_home_match_preferences_wizard_session";
import {getHomeMatchPreferencesSurveyBuildingHeight} from "../../utils/get_home_match_preferences_survey_form_values";
import {
    HomeMatchPreferencesWizardEntry,
    HomeMatchPreferencesWizardStep,
    IHomeMatchPreferencesWizardSurvey
} from "../../utils/get_home_match_preferences_wizard_steps";
import {IHomeMatchPreferencesWizardSummaryRegion} from "../../utils/get_home_match_preferences_wizard_summary_labels";
import {getHomeMatchFloorsPreferences} from "../../utils/home_match_floors_preference";
import {HomeMatchPreferencesWizardBudgetStep} from "./HomeMatchPreferencesWizardBudgetStep";
import {HomeMatchPreferencesWizardCompletionDateStep} from "./HomeMatchPreferencesWizardCompletionDateStep";
import {HomeMatchPreferencesWizardLocationStep} from "./HomeMatchPreferencesWizardLocationStep";
import {HomeMatchPreferencesWizardModal} from "./HomeMatchPreferencesWizardModal";
import {HomeMatchPreferencesWizardPhoneStep} from "./HomeMatchPreferencesWizardPhoneStep";
import {HomeMatchPreferencesWizardRoomsStep} from "./HomeMatchPreferencesWizardRoomsStep";
import {HomeMatchPreferencesWizardSmsDescription} from "./HomeMatchPreferencesWizardSmsDescription";
import {HomeMatchPreferencesWizardSmsStep} from "./HomeMatchPreferencesWizardSmsStep";
import {HomeMatchPreferencesWizardSummaryStep} from "./HomeMatchPreferencesWizardSummaryStep";

const {BUDGET, COMPLETION_DATE, LOCATION, PHONE, ROOMS, SMS, SUMMARY} = HomeMatchPreferencesWizardStep;
const {
    BUDGET_DESCRIPTION,
    BUDGET_TITLE,
    LOCATION_DESCRIPTION,
    LOCATION_TITLE,
    PHONE_DESCRIPTION,
    PHONE_TITLE,
    ROOMS_DESCRIPTION,
    ROOMS_TITLE,
    SMS: SMS_CONTENT,
    SUMMARY: SUMMARY_CONTENT
} = HOME_MATCH_PREFERENCES_WIZARD_CONTENT;

const STEP_INTRO: Partial<Record<HomeMatchPreferencesWizardStep, {description?: string; title: string}>> = {
    [BUDGET]: {description: BUDGET_DESCRIPTION, title: BUDGET_TITLE},
    [COMPLETION_DATE]: {
        description: HOME_MATCH_PREFERENCES_WIZARD_CONTENT.COMPLETION_DATE_DESCRIPTION,
        title: HOME_MATCH_PREFERENCES_SURVEY_CONTENT.COMPLETION_DATE
    },
    [LOCATION]: {description: LOCATION_DESCRIPTION, title: LOCATION_TITLE},
    [PHONE]: {description: PHONE_DESCRIPTION, title: PHONE_TITLE},
    [ROOMS]: {description: ROOMS_DESCRIPTION, title: ROOMS_TITLE},
    [SMS]: {title: SMS_CONTENT.TITLE},
    [SUMMARY]: {description: SUMMARY_CONTENT.DESCRIPTION, title: SUMMARY_CONTENT.TITLE}
};

interface IProps {
    additionalAreas?: number[] | null;
    amplitudeLocation?: HomeMatchAmplitudeLocation;
    area?: IRange<number | null> | null;
    entry?: HomeMatchPreferencesWizardEntry;
    financingPreference: HomeMatchFinancingPreference | null;
    isOpen: boolean;
    onClose: () => void;
    onOpenFullSurvey?: () => void;
    returnsToSummary?: boolean;
    onSurveyStepsDone: () => void;
    phone: string | null;
    preferencesSurveySource: PreferencesSurveySource;
    regionDetails?: IHomeMatchPreferencesWizardSummaryRegion[];
    regionOptions: IOption<number>[];
    steps: HomeMatchPreferencesWizardStep[];
    survey: IHomeMatchPreferencesWizardSurvey | null;
    zIndex?: number;
}

export const HomeMatchPreferencesWizardSurvey = (props: IProps) => {
    const {
        additionalAreas = null,
        amplitudeLocation = HomeMatchAmplitudeLocation.DASHBOARD,
        area = null,
        isOpen,
        onOpenFullSurvey,
        regionDetails = [],
        survey,
        zIndex
    } = props;
    const {
        canGoBack,
        closeWithoutSaving,
        formMethods,
        goBack,
        isExitSaveDisabled,
        isParameterEntry,
        isPrimaryDisabled,
        showExitPrompt,
        isVisible,
        map,
        onPrimaryClick,
        phone,
        primaryLabel,
        progressStepIndex,
        saveAndClose,
        sms,
        stepCount,
        summary,
        editingStep,
        visibleStep
    } = useHomeMatchPreferencesWizardSession({...props, amplitudeLocation});

    if (!isVisible) return null;

    const stepIntro = STEP_INTRO[visibleStep];
    const stepDescription =
        visibleStep === SMS && sms.verifiedPhone ? (
            <HomeMatchPreferencesWizardSmsDescription isSubmitting={phone.isSubmitting} onChangeNumber={sms.changeNumber} phone={sms.verifiedPhone} />
        ) : (
            stepIntro?.description
        );

    return (
        <FormProviderRHF {...formMethods}>
            <HomeMatchPreferencesWizardModal
                amplitudeLocation={amplitudeLocation}
                canGoBack={canGoBack}
                // Keeps the SMS step mounted while the number is being changed.
                contentKey={sms.isMounted ? SMS : undefined}
                description={stepDescription}
                isExitSaveDisabled={isExitSaveDisabled}
                isOpen={isOpen}
                isParameterEntry={isParameterEntry}
                showExitPrompt={showExitPrompt}
                isPrimaryDisabled={isPrimaryDisabled}
                mapActions={map.actions}
                onBack={goBack}
                onCloseWithoutSaving={closeWithoutSaving}
                onExitSave={saveAndClose}
                onPrimaryClick={onPrimaryClick}
                onRequestClose={phone.isChanging ? phone.closeChange : editingStep ? goBack : undefined}
                primaryLabel={primaryLabel}
                progressStepIndex={progressStepIndex}
                step={visibleStep}
                stepCount={stepCount}
                title={stepIntro?.title}
                zIndex={zIndex}
            >
                <div className={stepStyle}>
                    {visibleStep === PHONE && (
                        <HomeMatchPreferencesWizardPhoneStep
                            onRegisterSubmit={phone.registerSubmit}
                            onSaved={phone.onSaved}
                            onSubmittingChange={phone.setIsSubmitting}
                        />
                    )}

                    {visibleStep === LOCATION && (
                        <HomeMatchPreferencesWizardLocationStep
                            isMapOpen={map.isActive}
                            onMapActiveChange={map.setIsActive}
                            onRegisterMapActions={map.registerActions}
                        />
                    )}

                    {visibleStep === BUDGET && <HomeMatchPreferencesWizardBudgetStep />}

                    {visibleStep === COMPLETION_DATE && <HomeMatchPreferencesWizardCompletionDateStep />}

                    {visibleStep === ROOMS && <HomeMatchPreferencesWizardRoomsStep />}

                    {visibleStep === SUMMARY && (
                        <HomeMatchPreferencesWizardSummaryStep
                            consent={summary.consent}
                            onChangeProperty={() => onOpenFullSurvey?.()}
                            onChangeStep={summary.onChangeStep}
                            onConsentChange={summary.setConsent}
                            summary={{
                                ...summary.saved,
                                additionalAreas,
                                area,
                                buildingHeight: getHomeMatchPreferencesSurveyBuildingHeight(survey),
                                constructionEndDate: survey?.construction_end_date ?? null,
                                floorsPreference: getHomeMatchFloorsPreferences(survey),
                                marketTypes: survey?.market_types ?? null,
                                regionDetails,
                                types: survey?.types ?? null
                            }}
                        />
                    )}

                    {sms.isMounted &&
                        sms.verifiedPhone && (
                            // Stays mounted while the number is being changed, so going back does not resend the code.
                            <div hidden={visibleStep === PHONE}>
                                <HomeMatchPreferencesWizardSmsStep
                                    key={sms.sessionKey}
                                    onCanConfirmChange={sms.setCanConfirm}
                                    onRegisterSubmit={sms.registerSubmit}
                                    onResend={sms.onResend}
                                    onSubmittingChange={phone.setIsSubmitting}
                                    onSuccess={sms.onSuccess}
                                />
                            </div>
                        )}
                </div>
            </HomeMatchPreferencesWizardModal>
        </FormProviderRHF>
    );
};

const stepStyle = css({
    display: "flex",
    flex: "1 1 auto",
    flexDirection: "column",
    gap: 2,
    minHeight: 0
});
